// #ux-redesign-start: Spinner primitive (Paso 5 - Sistema de carga unificado)
// Spinner ECG (electrocardiograma) — único motor de "loading" en pantallas
// rediseñadas. Reemplaza:
//   - LoadingLocal (cuadrado amarillo del plugin legacy)
//   - LoadingGlobal (sk-chase naranja en blanco hardcoded)
//   - Reactstrap <Spinner>
//
// Diseño aportado por el equipo de UI (motivo: latido ECG = identidad médica).
//
// CARACTERÍSTICAS
//   - Animación CSS keyframe (no JS), respeta prefers-reduced-motion.
//   - Fade en bordes via mask-image → drop-in sobre cualquier fondo (no
//     necesita prop bg como el demo original).
//   - Color via token --color-spinner-stroke, theme-aware (verde médico).
//   - Tamaños sm / md / lg consistentes con resto del DS.
//   - i18n-agnóstico: el caller pasa label/ariaLabel ya traducidos.
//
// USO
//   <Spinner />                                // 120×40, sin texto
//   <Spinner size="sm" />                      // dentro de inputs/inline
//   <Spinner size="lg" />                      // pantallas vacías grandes
//   <Spinner label={t('loading-studies')} />   // texto a la derecha
//   <Spinner ariaLabel="Loading patient" />    // sólo lector de pantalla
//
// PORTABILIDAD: trasplantable a Web-Patients sin cambios. Requiere los
// estilos en _ux-redesign-ds.scss y el token en _ux-redesign-tokens.scss.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import PropTypes from 'prop-types';

// Una onda ECG completa en viewBox 0 0 120 40 (baseline y=20).
// Duplicada en el track para que el loop sea seamless: track 200% de ancho,
// se desplaza -50% por iteración.
const ECG_POINTS = [
  '0,20',
  '14,20',
  '17,17', '19,17', '21,20',   // onda P
  '24,20',
  '27,28',                       // Q
  '29,2',                        // R
  '31,34',                       // S
  '33,20',
  '37,20',
  '40,16', '46,16', '49,20',   // onda T
  '60,20',
  '74,20',
  '77,17', '79,17', '81,20',
  '84,20',
  '87,28',
  '89,2',
  '91,34',
  '93,20',
  '97,20',
  '100,16', '106,16', '109,20',
  '120,20',
].join(' ');

const EcgWave = () => (
  <svg className="ds-spinner__wave" viewBox="0 0 120 40" preserveAspectRatio="none" aria-hidden="true">
    <polyline
      points={ECG_POINTS}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Spinner = ({
  size = 'md',
  label = null,
  ariaLabel = 'Laden…',
  className = '',
}) => {
  const spinnerEl = (
    <span
      className={`ds-spinner ds-spinner--${size} ${label ? '' : className}`.trim()}
      role={label ? undefined : 'status'}
      aria-label={label ? undefined : ariaLabel}
    >
      <span className="ds-spinner__track">
        <EcgWave />
        <EcgWave />
      </span>
    </span>
  );

  if (!label) return spinnerEl;

  return (
    <span
      className={`ds-spinner-with-label ${className}`.trim()}
      role="status"
      aria-label={ariaLabel || label}
    >
      {spinnerEl}
      <span className="ds-spinner-with-label__text">{label}</span>
    </span>
  );
};

Spinner.propTypes = {
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  label: PropTypes.node,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
};

export default Spinner;
// #ux-redesign-end
